import { useEffect, useMemo, useState } from "react";
import {
  listenToScheduledPosts,
  ScheduledPost,
} from "../../../../database/community/scheduledPosts";

// Live view of the owned scheduler queue plus the derived slices every
// section needs: drafts for the review queue, upcoming for the calendar and
// home, errors for retry surfacing, and a by-day grouping for the agenda.

export type ScheduledPostsState = {
  posts: ScheduledPost[];
  loading: boolean;
  /** Needs review, oldest due first. */
  drafts: ScheduledPost[];
  /** scheduled or publishing, soonest first. */
  upcoming: ScheduledPost[];
  /** error or partial, most recent first. */
  errors: ScheduledPost[];
  /** sent, partial, cancelled. */
  history: ScheduledPost[];
  /** cancelled only, most recent first. */
  cancelled: ScheduledPost[];
  /** upcoming + drafts grouped by local calendar day (YYYY-MM-DD), sorted. */
  byDay: { day: string; posts: ScheduledPost[] }[];
};

const dayKey = (iso: string | null): string => {
  if (!iso) return "unscheduled";
  const d = new Date(iso);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
};

const byDueAsc = (a: ScheduledPost, b: ScheduledPost) =>
  (a.dueAt || "").localeCompare(b.dueAt || "");
const byDueDesc = (a: ScheduledPost, b: ScheduledPost) =>
  (b.dueAt || "").localeCompare(a.dueAt || "");

export const useScheduledPosts = (communityId?: string): ScheduledPostsState => {
  const [posts, setPosts] = useState<ScheduledPost[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!communityId) {
      setPosts([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    const unsub = listenToScheduledPosts(
      communityId,
      (next) => {
        setPosts(next);
        setLoading(false);
      },
      () => setLoading(false)
    );
    return () => unsub();
  }, [communityId]);

  return useMemo(() => {
    const drafts = posts.filter((p) => p.status === "draft").sort(byDueAsc);
    const upcoming = posts
      .filter((p) => p.status === "scheduled" || p.status === "publishing")
      .sort(byDueAsc);
    const errors = posts
      .filter((p) => p.status === "error" || p.status === "partial")
      .sort(byDueDesc);
    const history = posts
      .filter(
        (p) =>
          p.status === "sent" || p.status === "partial" || p.status === "cancelled"
      )
      .sort(byDueDesc);
    const cancelled = posts
      .filter((p) => p.status === "cancelled")
      .sort(byDueDesc);

    const groups = new Map<string, ScheduledPost[]>();
    [...upcoming, ...drafts].forEach((p) => {
      const key = dayKey(p.dueAt);
      if (!groups.has(key)) groups.set(key, []);
      groups.get(key)!.push(p);
    });
    const byDay = Array.from(groups.entries())
      .sort(([a], [b]) => a.localeCompare(b))
      .map(([day, dayPosts]) => ({ day, posts: dayPosts.sort(byDueAsc) }));

    return { posts, loading, drafts, upcoming, errors, history, cancelled, byDay };
  }, [posts, loading]);
};

export default useScheduledPosts;
